import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type ImageLightboxProps = {
  src: string;
  alt?: string;
  /** Thumbnail: fits inside box without cropping wide/tall images */
  thumbMaxHeightClass?: string;
  thumbMaxWidthClass?: string;
  hint?: string;
};

/**
 * Thumbnail preview with click to open full image in an overlay (Escape / backdrop to close).
 */
export function ImageLightbox({
  src,
  alt = "",
  thumbMaxHeightClass = "max-h-36",
  thumbMaxWidthClass = "max-w-full",
  hint = "Click for full size",
}: ImageLightboxProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="group block w-full max-w-full text-left"
      >
        <span className="block overflow-hidden rounded-xl border border-brand-gray bg-brand-gray/15">
          <img
            src={src}
            alt={alt}
            className={`mx-auto block ${thumbMaxHeightClass} ${thumbMaxWidthClass} w-auto object-contain`}
          />
        </span>
        <span className="mt-1.5 block text-xs text-brand-blue/65 group-hover:text-brand-maroon">
          {hint}
        </span>
      </button>
      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label="Full size image"
          >
            <button
              type="button"
              className="absolute inset-0 bg-black/75"
              aria-label="Close"
              onClick={() => setOpen(false)}
            />
            <button
              type="button"
              className="fixed right-3 top-3 z-[102] rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-brand-blue shadow-lg sm:right-5 sm:top-5"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
            <div className="relative z-10 flex max-h-[min(92vh,1200px)] max-w-[min(96vw,1400px)] items-center justify-center">
              <img
                src={src}
                alt={alt}
                className="max-h-[min(92vh,1200px)] max-w-full object-contain shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
